import React, { useEffect, useRef, useState } from "react";
import "../../styles/offsetting.css";
import loader2 from "../../images/loader_2.gif";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import { convertTo12HourPlusDateFormat } from "../../util/helperFunctions";
import "../../styles/ccar-form.css";
import "../../styles/ccar-v2.css";

const departments = [
  "Finance",
  "Sales",
  "Warehouse / Supply Chain",
  "Customer Fulfillment and Delivery",
  "Other",
];

const ncSources = [
  {
    value: "Delivery (Customer Complaint)",
    help: "Late, short or wrong delivery reported by a customer",
  },
  {
    value: "Quality (Customer Complaint)",
    help: "Damaged or defective items reported by a customer",
  },
  {
    value: "Audit",
    help: "Finding raised during an internal or external audit",
  },
  {
    value: "Incident Report",
    help: "Issue logged through an incident report",
  },
];

const warehouses = [
  "Bacolod",
  "Bohol",
  "Cavite Depot",
  "NCR",
  "Ormoc",
  "Starport Mindoro",
  "Tacloban",
  "Top Armada Mindoro",
  "Other",
];

const ACCEPTED_FILES = "image/*,.pdf,.doc,.docx,.xls,.xlsx";
const ACCEPTED_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Order in which errors are checked, and the element to focus for each.
const ERROR_FIELDS = [
  ["issuedTo", "ccar-issued-to"],
  ["issuedToOther", "ccar-issued-to-other"],
  ["warehouse", "ccar-warehouse"],
  ["warehouseOther", "ccar-warehouse-other"],
  ["ncSource", "ccar-nc-source-0"],
  ["email", "ccar-email"],
];

const isAcceptedFile = (file) => {
  if (file.type && file.type.startsWith("image/")) {
    return true;
  }

  const extension = file.name.split(".").pop().toLowerCase();

  return ACCEPTED_EXTENSIONS.includes(extension);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileBadge = (file) => {
  const extension = file.name.includes(".")
    ? file.name.split(".").pop().toUpperCase()
    : "FILE";

  return extension.slice(0, 4);
};

const isEditableElement = (element) =>
  element instanceof HTMLElement &&
  (element.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName));

// Screenshots and images copied from browsers arrive as "image.png", so give
// them a unique name to tell several pasted images apart.
const renamePastedFile = (file, index) => {
  if (file.name && !/^image\.\w+$/i.test(file.name)) {
    return file;
  }

  const extension = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const stamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "-")
    .slice(0, 15);

  return new File([file], `pasted-image-${stamp}-${index + 1}.${extension}`, {
    type: file.type,
    lastModified: Date.now(),
  });
};

let attachmentSeed = 0;

function CcarForm(props) {
  const { formDetails, setReferenceId, setDateAndTime, setFormSubmitted } =
    props;

  const [isSubmitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [skippedFiles, setSkippedFiles] = useState(0);
  const [isDragging, setDragging] = useState(false);

  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  const pasteHandlerRef = useRef(null);

  const [formData, setFormData] = useState({
    issuedTo: "",
    issuedToOther: "",
    ncSource: "",
    warehouse: "",
    warehouseOther: "",
    referenceNumber: "",
    ncDescription: "",
    contactPerson: "",
    contactNumber: "",
    email: "",
  });

  const {
    issuedTo,
    issuedToOther,
    ncSource,
    warehouse,
    warehouseOther,
    referenceNumber,
    contactPerson,
    contactNumber,
    email,
    ncDescription,
  } = formData;

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Release image previews when the form goes away.
  useEffect(
    () => () => {
      attachmentsRef.current.forEach((attachment) => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    },
    [],
  );

  const clearError = (...keys) => {
    setErrors((previousErrors) => {
      if (!keys.some((key) => previousErrors[key])) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      keys.forEach((key) => delete nextErrors[key]);

      return nextErrors;
    });
  };

  const handleDataChange = (key, value) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      [key]: value,
    }));
    clearError(key);
  };

  const handleIssuedToChange = (value) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      issuedTo: value,
      issuedToOther: value === "Other" ? previousFormData.issuedToOther : "",
    }));
    clearError("issuedTo", "issuedToOther");
  };

  const handleWarehouseChange = (value) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      warehouse: value,
      warehouseOther: value === "Other" ? previousFormData.warehouseOther : "",
    }));
    clearError("warehouse", "warehouseOther");
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const accepted = files.filter(isAcceptedFile);

    setSkippedFiles(files.length - accepted.length);

    if (accepted.length === 0) {
      return;
    }

    const newAttachments = accepted.map((file) => {
      attachmentSeed += 1;

      return {
        key: `ccar-file-${attachmentSeed}`,
        file,
        previewUrl: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };
    });

    setAttachments((previousAttachments) => [
      ...previousAttachments,
      ...newAttachments,
    ]);
  };

  const handleFileInputChange = (event) => {
    addFiles(event.target.files);

    // Allows the user to select the same file again.
    event.target.value = "";
  };

  const removeAttachment = (key) => {
    const target = attachments.find((attachment) => attachment.key === key);

    if (target?.previewUrl) {
      URL.revokeObjectURL(target.previewUrl);
    }

    setAttachments((previousAttachments) =>
      previousAttachments.filter((attachment) => attachment.key !== key),
    );
  };

  // Lets the user paste a copied image (or file) anywhere on the form.
  pasteHandlerRef.current = (event) => {
    if (isSubmitting) {
      return;
    }

    const files = Array.from(event.clipboardData?.files || []);

    if (files.length === 0) {
      return;
    }

    // Leave pastes into other inputs on the page (e.g. the chat composer) alone.
    if (
      isEditableElement(event.target) &&
      !formRef.current?.contains(event.target)
    ) {
      return;
    }

    event.preventDefault();
    addFiles(files.map(renamePastedFile));
  };

  useEffect(() => {
    const handlePaste = (event) => pasteHandlerRef.current?.(event);

    document.addEventListener("paste", handlePaste);

    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const handleDragOver = (event) => {
    event.preventDefault();

    if (!isSubmitting && !isDragging) {
      setDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragging(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    if (!isSubmitting) {
      addFiles(event.dataTransfer.files);
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!issuedTo) {
      nextErrors.issuedTo = "Select the department this CCAR is issued to.";
    } else if (issuedTo === "Other" && !issuedToOther.trim()) {
      nextErrors.issuedToOther = "Enter the department name.";
    }

    if (!warehouse) {
      nextErrors.warehouse = "Select a warehouse.";
    } else if (warehouse === "Other" && !warehouseOther.trim()) {
      nextErrors.warehouseOther = "Enter the warehouse name.";
    }

    if (!ncSource) {
      nextErrors.ncSource = "Select where the nonconformity came from.";
    }

    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      nextErrors.email = "Enter a valid email address, like name@company.com.";
    }

    return nextErrors;
  };

  const focusFirstError = (nextErrors) => {
    const firstError = ERROR_FIELDS.find(([key]) => nextErrors[key]);

    if (!firstError) {
      return;
    }

    const element = document.getElementById(firstError[1]);

    if (!element) {
      return;
    }

    const scrollTarget = element.closest(".ccr-field") || element;

    scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    element.focus({ preventScroll: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!formDetails?.id) {
      showSwal("Unable to identify the selected CCAR form.");
      return;
    }

    const nextErrors = validateForm();

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      return;
    }

    /*
     * Use the custom input when Other is selected.
     * Otherwise, use the dropdown value.
     */
    const finalIssuedTo =
      issuedTo === "Other" ? issuedToOther.trim() : issuedTo.trim();

    const finalWarehouse =
      warehouse === "Other" ? warehouseOther.trim() : warehouse.trim();

    const requestFormData = new FormData();

    requestFormData.append("issued_to", finalIssuedTo);

    requestFormData.append("nc_source", ncSource);

    requestFormData.append("warehouse", finalWarehouse);

    requestFormData.append("reference_number", referenceNumber.trim());

    requestFormData.append("contact_person", contactPerson.trim());

    requestFormData.append("contact_number", contactNumber.trim());

    requestFormData.append("email", email.trim());

    requestFormData.append("nc_description", ncDescription.trim());

    attachments.forEach(({ file }) => {
      requestFormData.append("prospecting_images", file);
    });

    setSubmitting(true);

    try {
      const response = await ApiService.forms.submitCCARRequest(
        formDetails.id,
        requestFormData,
      );

      if (!response) {
        showSwal("No response received from server.");
        return;
      }

      const { success, ccar_request } = response;

      if (!success) {
        showSwal(common.errors.SUBMIT_OFFSET_FORM_ERROR);
        return;
      }

      if (!ccar_request) {
        showSwal(
          "CCAR request was submitted, but the response data is incomplete.",
        );
        return;
      }

      setReferenceId(ccar_request.reference_id);

      setDateAndTime(convertTo12HourPlusDateFormat(ccar_request.createdAt));

      setFormSubmitted(true);
    } catch (error) {
      showSwal("Failed to submit CCAR request.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (key) => `ccr-field${errors[key] ? " has-error" : ""}`;

  const errorText = (key) =>
    errors[key] ? (
      <p className="ccr-error" id={`ccar-${key}-error`} role="alert">
        {errors[key]}
      </p>
    ) : null;

  const errorProps = (key) =>
    errors[key]
      ? { "aria-invalid": true, "aria-describedby": `ccar-${key}-error` }
      : {};

  return (
    <div className="ccr">
      <form
        ref={formRef}
        className="ccr-sheet"
        onSubmit={handleSubmit}
        noValidate
      >
        <header className="ccr-head">
          <h2 className="ccr-title">Customer Corrective Action Request</h2>
          <p className="ccr-subtitle">
            Log a nonconformity and route it to the team that needs to act on
            it.
          </p>
        </header>

        {/* Assignment */}
        <section className="ccr-section" aria-labelledby="ccar-sec-assignment">
          <div className="ccr-section-head">
            <h3 className="ccr-section-title" id="ccar-sec-assignment">
              Assignment
            </h3>
            <p className="ccr-section-help">
              Who should respond, and which warehouse this concerns.
            </p>
          </div>

          <div className="ccr-grid">
            <div className={fieldClass("issuedTo")}>
              <label className="ccr-label" htmlFor="ccar-issued-to">
                Issued to<span className="ccr-req">*</span>
              </label>

              <select
                id="ccar-issued-to"
                className={`ccr-select${issuedTo ? "" : " is-placeholder"}`}
                value={issuedTo}
                disabled={isSubmitting}
                onChange={(event) => handleIssuedToChange(event.target.value)}
                {...errorProps("issuedTo")}
              >
                <option value="">Select department</option>

                {departments.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>

              {errorText("issuedTo")}

              {issuedTo === "Other" && (
                <div className={`ccr-other ${fieldClass("issuedToOther")}`}>
                  <label
                    className="ccr-label"
                    htmlFor="ccar-issued-to-other"
                  >
                    Department name<span className="ccr-req">*</span>
                  </label>

                  <input
                    id="ccar-issued-to-other"
                    className="ccr-input"
                    type="text"
                    value={issuedToOther}
                    placeholder="e.g. Purchasing"
                    disabled={isSubmitting}
                    autoFocus
                    onChange={(event) =>
                      handleDataChange("issuedToOther", event.target.value)
                    }
                    {...errorProps("issuedToOther")}
                  />

                  {errorText("issuedToOther")}
                </div>
              )}
            </div>

            <div className={fieldClass("warehouse")}>
              <label className="ccr-label" htmlFor="ccar-warehouse">
                Warehouse<span className="ccr-req">*</span>
              </label>

              <select
                id="ccar-warehouse"
                className={`ccr-select${warehouse ? "" : " is-placeholder"}`}
                value={warehouse}
                disabled={isSubmitting}
                onChange={(event) => handleWarehouseChange(event.target.value)}
                {...errorProps("warehouse")}
              >
                <option value="">Select warehouse</option>

                {warehouses.map((warehouseItem) => (
                  <option key={warehouseItem} value={warehouseItem}>
                    {warehouseItem}
                  </option>
                ))}
              </select>

              {errorText("warehouse")}

              {warehouse === "Other" && (
                <div className={`ccr-other ${fieldClass("warehouseOther")}`}>
                  <label
                    className="ccr-label"
                    htmlFor="ccar-warehouse-other"
                  >
                    Warehouse name<span className="ccr-req">*</span>
                  </label>

                  <input
                    id="ccar-warehouse-other"
                    className="ccr-input"
                    type="text"
                    value={warehouseOther}
                    placeholder="e.g. Iloilo"
                    disabled={isSubmitting}
                    autoFocus
                    onChange={(event) =>
                      handleDataChange("warehouseOther", event.target.value)
                    }
                    {...errorProps("warehouseOther")}
                  />

                  {errorText("warehouseOther")}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* NC Source */}
        <section className="ccr-section" aria-labelledby="ccar-sec-source">
          <div className="ccr-section-head">
            <h3 className="ccr-section-title" id="ccar-sec-source">
              NC source<span className="ccr-req">*</span>
            </h3>
            <p className="ccr-section-help">
              Where the nonconformity was first raised.
            </p>
          </div>

          <div className={fieldClass("ncSource")}>
            <div
              className="ccr-choices"
              role="radiogroup"
              aria-labelledby="ccar-sec-source"
              {...errorProps("ncSource")}
            >
              {ncSources.map((source, index) => {
                const isChecked = ncSource === source.value;

                return (
                  <label
                    key={source.value}
                    className={`ccr-choice${isChecked ? " is-checked" : ""}${
                      isSubmitting ? " is-disabled" : ""
                    }`}
                  >
                    <input
                      id={`ccar-nc-source-${index}`}
                      className="ccr-choice-input"
                      type="radio"
                      name="ccar-nc-source"
                      value={source.value}
                      checked={isChecked}
                      disabled={isSubmitting}
                      onChange={(event) =>
                        handleDataChange("ncSource", event.target.value)
                      }
                    />
                    <span className="ccr-dot" aria-hidden="true" />
                    <span className="ccr-choice-text">
                      <span className="ccr-choice-title">{source.value}</span>
                      <span className="ccr-choice-help">{source.help}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {errorText("ncSource")}
          </div>
        </section>

        {/* Reference & Contact */}
        <section className="ccr-section" aria-labelledby="ccar-sec-contact">
          <div className="ccr-section-head">
            <h3 className="ccr-section-title" id="ccar-sec-contact">
              Reference &amp; contact
            </h3>
            <p className="ccr-section-help">
              Optional details that help the team trace and follow up.
            </p>
          </div>

          <div className="ccr-grid">
            <div className="ccr-field ccr-span-2">
              <label className="ccr-label" htmlFor="ccar-reference-number">
                Customer / audit reference number
                <span className="ccr-optional">Optional</span>
              </label>

              <input
                id="ccar-reference-number"
                className="ccr-input"
                type="text"
                value={referenceNumber}
                placeholder="e.g. SO-102394 or audit ref"
                disabled={isSubmitting}
                onChange={(event) =>
                  handleDataChange("referenceNumber", event.target.value)
                }
              />
            </div>

            <div className="ccr-field">
              <label className="ccr-label" htmlFor="ccar-contact-person">
                Contact person<span className="ccr-optional">Optional</span>
              </label>

              <input
                id="ccar-contact-person"
                className="ccr-input"
                type="text"
                value={contactPerson}
                placeholder="Full name"
                autoComplete="name"
                disabled={isSubmitting}
                onChange={(event) =>
                  handleDataChange("contactPerson", event.target.value)
                }
              />
            </div>

            <div className="ccr-field">
              <label className="ccr-label" htmlFor="ccar-contact-number">
                Contact number<span className="ccr-optional">Optional</span>
              </label>

              <input
                id="ccar-contact-number"
                className="ccr-input"
                type="tel"
                inputMode="tel"
                value={contactNumber}
                placeholder="e.g. 0917 123 4567"
                autoComplete="tel"
                disabled={isSubmitting}
                onChange={(event) =>
                  handleDataChange("contactNumber", event.target.value)
                }
              />
            </div>

            <div className={`${fieldClass("email")} ccr-span-2`}>
              <label className="ccr-label" htmlFor="ccar-email">
                Email address<span className="ccr-optional">Optional</span>
              </label>

              <input
                id="ccar-email"
                className="ccr-input"
                type="email"
                inputMode="email"
                value={email}
                placeholder="name@company.com"
                autoComplete="email"
                disabled={isSubmitting}
                onChange={(event) =>
                  handleDataChange("email", event.target.value)
                }
                {...errorProps("email")}
              />

              {errorText("email")}
            </div>
          </div>
        </section>

        {/* Pagtugon sa NC */}
        <section className="ccr-section" aria-labelledby="ccar-sec-response">
          <div className="ccr-section-head">
            <h3 className="ccr-section-title" id="ccar-sec-response">
              Pagtugon sa NC
            </h3>
            <p className="ccr-section-help">
              Describe what went wrong and attach any supporting photos or
              files.
            </p>
          </div>

          <div className="ccr-grid">
            <div className="ccr-field ccr-span-2">
              <label className="ccr-label" htmlFor="ccar-nc-description">
                A. Pagsalaysay sa Paltos (NC) na Delivery or Quality
              </label>

              <textarea
                id="ccar-nc-description"
                className="ccr-textarea"
                value={ncDescription}
                placeholder="What happened, when, and which items or orders were affected"
                disabled={isSubmitting}
                aria-describedby="ccar-nc-description-count"
                onChange={(event) =>
                  handleDataChange("ncDescription", event.target.value)
                }
              />

              <div className="ccr-meta">
                <span
                  className="ccr-count"
                  id="ccar-nc-description-count"
                  aria-live="polite"
                >
                  {ncDescription.length.toLocaleString()}{" "}
                  {ncDescription.length === 1 ? "character" : "characters"}
                </span>
              </div>
            </div>

            <div className="ccr-field ccr-span-2">
              <span className="ccr-label" id="ccar-attachments-label">
                Attachments<span className="ccr-optional">Optional</span>
              </span>

              <div
                className={`ccr-drop${isDragging ? " is-over" : ""}${
                  isSubmitting ? " is-disabled" : ""
                }`}
                onDragEnter={handleDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <span className="ccr-drop-icon" aria-hidden="true">
                  <i className="fa fa-paperclip" />
                </span>

                <div className="ccr-drop-text">
                  <p className="ccr-drop-title">
                    Drop, paste or browse files
                  </p>
                  <p className="ccr-drop-help">
                    Images, PDF, Word or Excel. You can add several. Copy an
                    image and press Ctrl+V to attach it.
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  id="ccar-upload"
                  className="ccr-file-input"
                  type="file"
                  multiple
                  accept={ACCEPTED_FILES}
                  tabIndex={-1}
                  aria-hidden="true"
                  disabled={isSubmitting}
                  onChange={handleFileInputChange}
                />

                <button
                  type="button"
                  className="ccr-btn-ghost"
                  disabled={isSubmitting}
                  aria-describedby="ccar-attachments-label"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse files
                </button>
              </div>

              {skippedFiles > 0 && (
                <p className="ccr-error" role="alert">
                  {skippedFiles === 1
                    ? "1 file was skipped because its type isn't supported."
                    : `${skippedFiles} files were skipped because their type isn't supported.`}
                </p>
              )}

              {attachments.length > 0 && (
                <ul className="ccr-files" aria-label="Selected attachments">
                  {attachments.map(({ key, file, previewUrl }) => (
                    <li key={key} className="ccr-file">
                      {previewUrl ? (
                        <img className="ccr-thumb" src={previewUrl} alt="" />
                      ) : (
                        <span className="ccr-thumb" aria-hidden="true">
                          {fileBadge(file)}
                        </span>
                      )}

                      <span className="ccr-file-text">
                        <span className="ccr-file-name" title={file.name}>
                          {file.name}
                        </span>
                        <span className="ccr-file-size">
                          {formatFileSize(file.size)}
                        </span>
                      </span>

                      <button
                        type="button"
                        className="ccr-remove"
                        disabled={isSubmitting}
                        onClick={() => removeAttachment(key)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <i className="fa fa-trash" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        <div className="ccr-actions">
          <p className="ccr-actions-note">
            Fields marked <span className="ccr-req">*</span> are required.
          </p>

          <button
            type="submit"
            className="ccr-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <img src={loader2} alt="" className="ccr-spinner" />
            )}
            {isSubmitting ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CcarForm;
